import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { PolicyChangeService } from 'gw-capability-gateway';
import MLRenewalsReportAdditionalFilter from './MLRenewalsReportAdditionalFilter';
import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLRenewalsReportComponent.messages';
import metadata from './MLRenewalsReportComponent.metadata.json5';

const MLRenewalsReportComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history,
        formatAmountString
    } = props;
    const translator = useTranslator();

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const loadRenewalsData = useCallback(async (filteringData) => {
        updateIsLoading(true);

        const response = isDashboard
            ? await PolicyChangeService.getRenewalJobsForCurrentUser(rowLimit, filteringData, authHeader)
            : await PolicyChangeService.getRenewalJobsForCurrentUser(null, filteringData, authHeader);

        const filteredResp = _.filter(response, (item) => {
            return new Date(item.policyEffectiveDate) >= new Date()
        });
        updateFilteredJobs(filteredResp);
        updateIsLoading(false);
        updateResultsRowCount(filteredResp.length);
    }, [authHeader, isDashboard, rowLimit]);

    useEffect(() => {
        loadRenewalsData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilter = () => {
        loadRenewalsData(filterData);
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const renderRenewalDateCell = (item) => {
        return item.policyEffectiveDate ? moment(item.policyEffectiveDate).format('MM/DD/YYYY') : '-';
    };

    const renderCustomerName = (item) => {
        if(_.isUndefined(item.displayName_ML)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder_ML))}
            </Link>
        )
    };

    const renderProductCell = (item) => {
        return item.product.productName ? item.product.productName : '-';
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderCurrentCell = (item) => {
        if (_.get(item, 'currentTotalPremiumRPT_ML.amount')) {
            return formatAmountString(item.currentTotalPremiumRPT_ML);
        }
        return '-';
    };

    const renderNewCell = (item) => {
        if (_.get(item, 'newTotalPremiumRPT_ML.amount')) {
            return formatAmountString(item.newTotalPremiumRPT_ML);
        }
        return '-';
    };

    const renderChangeCell = (item) => {
        const additionalSign = item.premiumChangePercentage_ML >= 0 ? '+' : '';
        const premiumChangePercentage = `${additionalSign}${item.premiumChangePercentage_ML}%`;
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {premiumChangePercentage}
            </Link>
        )
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'renewals-report';
        history.push('/activity-reports', {reportView : reportView} );
    }

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        renewalsResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            additionalFiltersComponent: MLRenewalsReportAdditionalFilter
        },
        renewalsReportTable: {
            data: _.orderBy(filteredJobs, 'policyEffectiveDate', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noRecentPolicyRenewalsFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: translator(messages.Renewals)
        },
        viewAllRenewalsButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_renewals"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderRenewalDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderCurrentCell,
            renderNewCell,
            renderChangeCell
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLRenewalsReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    formatAmountString: PropTypes.func.isRequired
}

export default withRouter(MLRenewalsReportComponent);
